import axios from 'axios';
import type { AxiosRequestHeaders } from 'axios';
import type {
  AgentModelsResponse,
  ContentGenerateRequest,
  ImageGenerateRequest,
  ImageFromTopicRequest,
  ImageFormatInfo,
} from '../interface/agent.interface';
import { getToken } from '../utility/tokenManager';
import { getCurrentLang } from './langHeader';

/**
 * Axios instance for the Marketing Agent API.
 * Uses VITE_AGENT_API_URL env var, or falls back to Flask default.
 */
const agentHttp = axios.create({
  baseURL: import.meta.env.VITE_AGENT_API_URL || 'http://localhost:5000',
  timeout: 120_000, // LLM calls can be slow
  headers: { 'Content-Type': 'application/json' },
});

// ── M2M Auth: attach user JWT to every request ──────────────────────
agentHttp.interceptors.request.use((config) => {
  const token = getToken('accessToken');
  console.log('[agentApi interceptor] token present:', !!token, 'url:', config.url);
  if (token) {
    // Ensure headers is a proper object compatible with Axios types
    const headers = (config.headers ?? {}) as AxiosRequestHeaders;
    // Set Authorization header on the typed headers object
    headers.Authorization = `Bearer ${token}`;
    config.headers = headers;
  } else {
    console.warn('[agentApi interceptor] No token found — request will be unauthenticated');
  }
  const headers = (config.headers ?? {}) as AxiosRequestHeaders;
  headers['Accept-Language'] = getCurrentLang();
  config.headers = headers;
  return config;
});

// ── Handle 401 centrally ────────────────────────────────────────────
agentHttp.interceptors.response.use(
  (res) => {
    console.log('[agentApi interceptor] Response OK:', res.status, res.config.url);
    return res;
  },
  (error) => {
    console.error(
      '[agentApi interceptor] Response error:',
      error?.response?.status,
      error?.config?.url,
      error?.message,
    );
    if (error?.response?.status === 401) {
      console.warn('[agentApi interceptor] 401 — redirecting to /login');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

// ── Agent endpoints ─────────────────────────────────────────────────

export const agentApi = {
  /** GET /api/agent/models — list available LLM models */
  getModels: () => agentHttp.get<AgentModelsResponse>('/api/agent/models'),

  // ── Content generation endpoints ────────────────────────────────

  instagramCaption: (data: ContentGenerateRequest) =>
    agentHttp.post('/api/content/instagram/caption', data),

  instagramCarousel: (data: ContentGenerateRequest) =>
    agentHttp.post('/api/content/instagram/carousel', data),

  instagramReelScript: (data: ContentGenerateRequest) =>
    agentHttp.post('/api/content/instagram/reel-script', data),

  instagramStorySequence: (data: ContentGenerateRequest) =>
    agentHttp.post('/api/content/instagram/story-sequence', data),

  instagramHashtags: (data: { niche: string; count?: number }) =>
    agentHttp.post('/api/content/instagram/hashtags', data),

  adCopy: (data: ContentGenerateRequest) => agentHttp.post('/api/content/ads/copy', data),

  metaCampaign: (data: ContentGenerateRequest) =>
    agentHttp.post('/api/content/ads/meta-campaign', data),

  emailCopy: (data: ContentGenerateRequest) => agentHttp.post('/api/content/email', data),

  whatsappMessage: (data: ContentGenerateRequest) => agentHttp.post('/api/content/whatsapp', data),

  // ── Image generation endpoints ──────────────────────────────────

  getImageFormats: () => agentHttp.get<{ formats: ImageFormatInfo }>('/api/image/formats'),

  getActiveImageModel: () => agentHttp.get('/api/image/model'),

  generateImage: (data: ImageGenerateRequest) => agentHttp.post('/api/image/generate', data),

  generateImageFromTopic: (data: ImageFromTopicRequest) =>
    agentHttp.post('/api/image/generate-from-topic', data),
};

export default agentApi;
